class NewProductCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListener("linked-product-change", (e) =>
      this.handleProductChange(e)
    );
    this.quickViewElement = this.querySelector("quick-view");
    this.templateName = this.dataset.template ?? "product-card";
    this.quickAddTriggerElement = this.querySelector(
      "[data-quick-add-trigger]"
    );
    if (this.quickViewElement) {
      this.querySelectorAll(".product-card-gallery__image-container").forEach(
        (el) => {
          el.addEventListener("click", (e) => {
            e.preventDefault();
            this.quickViewElement.classList.add("active");
          });
        }
      );
    }
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-quick-add-trigger]")) {
        e.preventDefault();
        this.querySelector("quick-add")?.classList.add("active");
      }
    });
  }

  handleProductChange(e) {
    const productUrl = e.detail.productUrl;
    this.classList.add("loading");
    fetch(productUrl + "?view=" + this.templateName)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const productCard = doc.querySelector("new-product-card");
        this.innerHTML = productCard.innerHTML;
        this.classList.remove("loading");
        this.init();
      });
  }
}

if (!window.customElements.get("new-product-card")) {
  customElements.define("new-product-card", NewProductCard);
}
class ProductCardGallery extends HTMLElement {
  constructor() {
    super();
    this.activate = this.activate.bind(this);
    this.handleNavigationClick = this.handleNavigationClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.galleryImages === undefined) {
      this.initSwiper();
      return;
    }
    this.addEventListener("pointerenter", this.activate);
    this.addEventListener("pointerdown", this.activate, true);
    this.addEventListener("focusin", this.activate);
    this.addEventListener("click", this.handleNavigationClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener("pointerenter", this.activate);
    this.removeEventListener("pointerdown", this.activate, true);
    this.removeEventListener("focusin", this.activate);
    this.removeEventListener("click", this.handleNavigationClick, true);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  handleNavigationClick(event) {
    if (event.target.closest("[data-prev-btn], [data-next-btn]")) {
      event.preventDefault();
      this.activate();
    }
  }

  activate() {
    if (this.swiper || !this.getClientRects().length) return;
    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper || typeof Swiper === "undefined") return;
    if (this.dataset.galleryImages !== undefined) {
      const images = JSON.parse(this.dataset.galleryImages);
      const fragment = document.createDocumentFragment();
      images.forEach((image) => {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        const container = document.createElement("div");
        container.className = "product-card-gallery__image-container";
        const img = document.createElement("img");
        img.className = "w-full h-auto aspect-[375/530] object-cover";
        img.loading = "lazy";
        img.alt = image.alt || "";
        img.width = image.width;
        img.height = image.height;
        img.src = image.src;
        container.appendChild(img);
        const quickView = this.closest("new-product-card")?.querySelector(
          "quick-view"
        );
        if (quickView) {
          container.addEventListener("click", (event) => {
            event.preventDefault();
            quickView.classList.add("active");
          });
        }
        slide.appendChild(container);
        fragment.appendChild(slide);
      });
      wrapper.appendChild(fragment);
      delete this.dataset.galleryImages;
    }
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper) return;
    const prevBtn = this.querySelector("[data-prev-btn]");
    const nextBtn = this.querySelector("[data-next-btn]");
    this.swiper = new Swiper(this.querySelector(".swiper"), {
      slidesPerView: 1,
      spaceBetween: 10,
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      loop: this.querySelectorAll(".swiper-slide").length > 1,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      a11y: {
        enabled: true,
        prevSlideMessage:
          prevBtn?.getAttribute("aria-label") || "Go to the previous slide",
        nextSlideMessage:
          nextBtn?.getAttribute("aria-label") || "Go to the next slide",
      },
    });
  }
}
if (!window.customElements.get("product-card-gallery")) {
  customElements.define("product-card-gallery", ProductCardGallery);
}

class LinkedProductSwatch extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      this.dispatchEvent(
        new CustomEvent("linked-product-change", {
          detail: {
            productUrl: this.dataset.productUrl,
          },
          bubbles: true,
        })
      );
    });
  }
}

if (!window.customElements.get("linked-product-swatch")) {
  customElements.define("linked-product-swatch", LinkedProductSwatch);
}

class FeaturedCollectionSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    const prevBtn = this.querySelector("[data-fc-prev-btn]");
    const nextBtn = this.querySelector("[data-fc-next-btn]");
    this.swiper = new Swiper(this, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      mousewheel: {
        forceToAxis: true
      },
      freeMode: {
        enabled: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.8,
          slidesOffsetBefore: 20,
        },
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
    });
  }
}
if (!window.customElements.get("featured-collection-swiper")) {
  customElements.define("featured-collection-swiper", FeaturedCollectionSwiper);
}

class RelatedProductsSwiper extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    const prevBtn = this.querySelector("[data-rp-prev-btn]");
    const nextBtn = this.querySelector("[data-rp-next-btn]");
    this.swiper = new Swiper(this, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      mousewheel: {
        forceToAxis: true,
      },
      freeMode: {
        enabled: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.8,
          slidesOffsetBefore: 20,
        },
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
    });
  }
}
if (!window.customElements.get("related-products-swiper")) {
  customElements.define("related-products-swiper", RelatedProductsSwiper);
}

class FeaturedCollections extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.triggerElements = this.querySelectorAll("[data-collection-trigger]");
    this.targetElements = this.querySelectorAll("[data-collection-target]");
    this.triggerElements.forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleTriggerClick(e);
      });
    });
  }

  handleTriggerClick(e) {
    const collectionId = e.target.dataset.collectionTrigger;
    this.targetElements.forEach((el) => {
      el.classList.add("!hidden");
    });
    const collection = this.querySelector(
      `[data-collection-target="${collectionId}"]`
    );
    collection.classList.remove("!hidden");
    this.triggerElements.forEach((el) => {
      el.classList.remove("underline");
    });
    e.target.classList.add("underline");
  }
}

if (!window.customElements.get("featured-collections")) {
  customElements.define("featured-collections", FeaturedCollections);
}

class QuickAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.quickAddTriggerElement = this.querySelector(
      "[data-quick-add-trigger]"
    );
    this.quickAddTriggerElement?.addEventListener("click", (e) => {
      e.preventDefault();
      this.show();
    });
    this.querySelectorAll("[data-quick-add-close]").forEach((closeButton) => {
      closeButton.addEventListener("click", () => {
        this.hide();
        this.quickAddTriggerElement?.focus();
      });
    });
    this.addEventListener("mouseleave", () => {
      if (window.matchMedia("(hover: hover)").matches) {
        this.hide();
      }
    });

    document.addEventListener("scroll", () => {
      this.hide();
    });
  }

  show() {
    document.querySelectorAll("quick-add.active").forEach((quickAdd) => {
      if (quickAdd !== this) {
        typeof quickAdd.hide === "function"
          ? quickAdd.hide()
          : quickAdd.classList.remove("active");
      }
    });
    this.classList.add("active");
    document.documentElement.classList.add("quick-add-open");
  }

  hide() {
    this.classList.remove("active");
    if (!document.querySelector("quick-add.active")) {
      document.documentElement.classList.remove("quick-add-open");
    }
  }
}

if (!window.customElements.get("quick-add")) {
  customElements.define("quick-add", QuickAdd);
}

class QuickAddForm extends HTMLElement {
  constructor() {
    super();
  }

  get formId() {
    return this.getAttribute("id");
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.form = this.querySelector("form");
    this.masterSelect = this.querySelector("[name='id']");
    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener(
      "variant:change",
      this.handleVariantChange.bind(this)
    );
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
    this.addEventListener("add-to-cart", this.handleSubmit.bind(this));
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;
    this.masterSelect.value = variant.id;
  }

  handleSubmit(event) {
    event.preventDefault();
    this.submit();
  }

  submit() {
    const formData = new FormData(this.form);
    formData.append("id", this.masterSelect.value);
    formData.append("quantity", 1);
    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );

    fetch("/cart/add.js", {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
            },
          })
        );
        this.closest("quick-add")?.hide();
        document.querySelector('cart-drawer')?.show();
      })
      .catch((err) => console.error(err))
      .finally(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("theme:loading:end", { bubbles: true })
        );
      });
  }
}

if (!window.customElements.get("quick-add-form")) {
  window.customElements.define("quick-add-form", QuickAddForm);
}

class NewVariantPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.variants = JSON.parse(
      this.querySelector("[data-variants]").textContent
    );
    this.currentVariant = null;
    this.optionInputs = this.querySelectorAll("input");
    this.bindEvents();
  }

  bindEvents() {
    this.listenToChange();
  }

  listenToChange() {
    this.optionInputs.forEach((input) =>
      input.addEventListener("change", (e) => {
        e.preventDefault();
        this.updateCurrentVariant(e.target);
      })
    );
  }

  updateCheckedInputs() {
    this.optionInputs.forEach((input) => {
      if (input.checked) {
        input.setAttribute("checked", "checked");
      } else {
        input.removeAttribute("checked");
      }
    });
  }

  getSelectedValues() {
    return Array.from(this.querySelectorAll("input:checked")).reduce(
      (acc, input) => {
        acc[input.name] = input.value;
        return acc;
      },
      {}
    );
  }

  updateCurrentVariant(input = null) {
    const isAddOnClick = input?.dataset.addOnClick == "true";
    this.updateCheckedInputs();
    const selectedValues = this.getSelectedValues();

    this.currentVariant =
      this.variants.find((variant) => {
        return Object.entries(selectedValues).every(([name, value]) => {
          return variant[name] === value;
        });
      }) || null;

    if (this.currentVariant) {
      this.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: {
            variant: this.currentVariant,
          },
          bubbles: true,
        })
      );
    }

    if (isAddOnClick) {
      this.dispatchEvent(
        new CustomEvent("add-to-cart", {
          bubbles: true,
        })
      );
    }
  }
}

if (!window.customElements.get("new-variant-picker")) {
  window.customElements.define("new-variant-picker", NewVariantPicker);
}

class FeaturedCollectionList extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.triggerElements = this.querySelectorAll("[data-collection-trigger]");
    this.targetElements = this.querySelectorAll("[data-collection-target]");
    this.triggerElements.forEach((el) => {
      el.addEventListener("mouseover", (e) => {
        this.handleTriggerMouseover(e);
      });
    });
  }

  handleTriggerMouseover(e) {
    this.targetElements.forEach((el) => {
      el.classList.add("hidden");
    });
    const target = this.querySelector(
      `[data-collection-target="${e.target.dataset.collectionTrigger}"]`
    );
    target.classList.remove("hidden");
  }
}

if (!window.customElements.get("featured-collection-list")) {
  window.customElements.define(
    "featured-collection-list",
    FeaturedCollectionList
  );
}

class ReviewCardsSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.7,
          slidesOffsetBefore: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("review-cards-swiper")) {
  window.customElements.define("review-cards-swiper", ReviewCardsSwiper);
}

class CardsSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.15,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
      mousewheel: {
        forceToAxis: true
      },
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.15,
          spaceBetween: 16,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("cards-swiper")) {
  window.customElements.define("cards-swiper", CardsSwiper);
}

class TeamSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.15,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
      mousewheel: {
        forceToAxis: true,
      },
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      scrollbar: {
        el: this.querySelector(".swiper-scrollbar"),
        draggable: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.15,
          spaceBetween: 10,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("team-swiper")) {
  window.customElements.define("team-swiper", TeamSwiper);
}

class SlideshowSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper) {
      return;
    }

    if (!window.Swiper) {
      window.addEventListener("load", () => this.initSwiper(), { once: true });
      return;
    }

    this.classList.add("swiper");

    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper) {
      return;
    }

    const slides = wrapper.querySelectorAll(".swiper-slide");
    if (slides.length === 0) {
      requestAnimationFrame(() => this.initSwiper());
      return;
    }

    if (!this.querySelector(".swiper-pagination")) {
      const pagination = document.createElement("div");
      pagination.className = "swiper-pagination slideshow-pagination";
      pagination.setAttribute("aria-hidden", "true");
      this.appendChild(pagination);
    }

    const paginationEl = this.querySelector(".swiper-pagination");

    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      loop: slides.length > 1,
      pagination: {
        el: paginationEl,
        clickable: true,
        type: "bullets",
      },
      watchOverflow: false,
      observer: true,
      observeParents: true,
      autoplay: {
        delay: this.dataset.slideshowSpeed * 1000,
      },
    });

    this.swiper.update();
    if (this.swiper.pagination && paginationEl) {
      this.swiper.pagination.render();
      this.swiper.pagination.update();

      if (!paginationEl.querySelector(".swiper-pagination-bullet")) {
        this.initManualPagination(paginationEl, slides.length);
      }
    } else if (paginationEl) {
      this.initManualPagination(paginationEl, slides.length);
    }
  }

  initManualPagination(paginationEl, slideCount) {
    if (!this.swiper || paginationEl.dataset.manualPagination === "true") {
      return;
    }

    paginationEl.dataset.manualPagination = "true";
    paginationEl.innerHTML = "";

    for (let i = 0; i < slideCount; i += 1) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "slideshow-pagination__dot";
      dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
      dot.addEventListener("click", () => {
        if (this.swiper.params.loop) {
          this.swiper.slideToLoop(i);
        } else {
          this.swiper.slideTo(i);
        }
      });
      paginationEl.appendChild(dot);
    }

    const updateActiveDot = () => {
      const activeIndex =
        typeof this.swiper.realIndex === "number"
          ? this.swiper.realIndex
          : this.swiper.activeIndex;
      paginationEl
        .querySelectorAll(".slideshow-pagination__dot")
        .forEach((dot, index) => {
          dot.classList.toggle("is-active", index === activeIndex);
          dot.setAttribute("aria-current", index === activeIndex ? "true" : "false");
        });
    };

    this.swiper.on("slideChange", updateActiveDot);
    updateActiveDot();
  }
}

if (!window.customElements.get("slideshow-swiper")) {
  window.customElements.define("slideshow-swiper", SlideshowSwiper);
}

class ComingSoonSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.1,
      spaceBetween: 10,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 14,
      freeMode: true,
      breakpoints: {
        767: {
          slidesPerView: 3.1,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("coming-soon-slider")) {
  window.customElements.define("coming-soon-slider", ComingSoonSlider);
}

class WaitlistCountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
  }

  connectedCallback() {
    this.expirationDate = this.dataset.expirationDate;
    this.init();
  }

  init() {
    this.initCountdown();
  }

  initCountdown() {
    if (!this.expirationDate) {
      console.warn("No expiration date provided for countdown timer");
      return;
    }

    // Parse the expiration date
    // Expected format: "YYYY-MM-DD HH:MM" (e.g., "2025-12-25 12:00")
    const targetDate = new Date(this.expirationDate);

    if (isNaN(targetDate.getTime())) {
      console.error(
        'Invalid expiration date format. Expected: "YYYY-MM-DD HH:MM" (e.g., "2025-12-25 12:00")'
      );
      return;
    }

    // Start the countdown immediately
    this.updateCountdown(targetDate);

    // Update every second
    this.intervalId = setInterval(() => {
      this.updateCountdown(targetDate);
    }, 1000);
  }

  updateCountdown(targetDate) {
    const now = new Date();
    const timeLeft = targetDate.getTime() - now.getTime();

    if (timeLeft <= 0) {
      // Countdown has ended
      this.displayTime(0, 0, 0, 0);
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return;
    }

    // Calculate time components
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    this.displayTime(days, hours, minutes, seconds);
  }

  displayTime(days, hours, minutes, seconds) {
    const daysElement = this.querySelector("[data-days]");
    const hoursElement = this.querySelector("[data-hours]");
    const minutesElement = this.querySelector("[data-minutes]");
    const secondsElement = this.querySelector("[data-seconds]");

    if (daysElement) daysElement.textContent = days.toString().padStart(2, "0");
    if (hoursElement)
      hoursElement.textContent = hours.toString().padStart(2, "0");
    if (minutesElement)
      minutesElement.textContent = minutes.toString().padStart(2, "0");
    if (secondsElement)
      secondsElement.textContent = seconds.toString().padStart(2, "0");
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

if (!window.customElements.get("waitlist-countdown-timer")) {
  window.customElements.define(
    "waitlist-countdown-timer",
    WaitlistCountdownTimer
  );
}

class WaitlistCollectionCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleTriggerMouseOver = this.handleTriggerMouseOver.bind(this);
    this.init();
  }

  init() {
    this.triggerElements = this.querySelectorAll("[data-collection-trigger]");
    this.targetElements = this.querySelectorAll("[data-collection-target]");
    this.triggerElements.forEach((el) => {
      el.addEventListener("mouseenter", this.handleTriggerMouseOver);
    });
  }

  handleTriggerMouseOver(e) {
    const trigger = e.currentTarget;
    const targetId = trigger.getAttribute("data-collection-trigger");
    if (!targetId) return;
    this.targetElements.forEach((target) => target.classList.add("hidden"));
    const target = this.querySelector(`[data-collection-target="${targetId}"]`);
    target.classList.remove("hidden");
  }
}

if (!window.customElements.get("waitlist-collection-card")) {
  window.customElements.define(
    "waitlist-collection-card",
    WaitlistCollectionCard
  );
}

class WaysToWear extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.triggerElements = Array.from(this.querySelectorAll("[data-index]"));
    this.nameElements = Array.from(this.querySelectorAll("[data-name-index]"));
    this.initSwiper();
    this.addTriggerListeners();
  }

  initSwiper() {
    this.swiper = new Swiper(this.querySelector("[data-ways-to-wear-slider]"), {
      slidesPerView: 1.1,
      spaceBetween: 10,
      breakpoints: {
        767: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
      },
    });

    const triggerElements = this.triggerElements;
    this.swiper.on("slideChange", (e) => {
      const index = e.activeIndex;
      triggerElements.forEach((el) => {
        el.classList.add("opacity-50");
      });
      if (triggerElements[index]) {
        triggerElements[index].classList.remove("opacity-50");
      }

      this.updateNameElements(index);
    });
  }

  updateNameElements(index) {
    this.nameElements.forEach((el) => {
      el.classList.add("hidden");
    });
    if (this.nameElements[index]) {
      this.nameElements[index].classList.remove("hidden");
    }
  }

  addTriggerListeners() {
    if (!this.triggerElements || !this.swiper) return;
    this.triggerElements.forEach((el, idx) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.swiper.slideTo(idx);
      });
    });
  }
}

if (!window.customElements.get("ways-to-wear")) {
  window.customElements.define("ways-to-wear", WaysToWear);
}

class KlaviyoForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.form = this.querySelector("form");
    this.apiKey = this.dataset.apiKey;
    this.listId = this.dataset.listId;
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
  }

  handleSubmit(event) {
    event.preventDefault();
    this.submit();
  }

  submit() {
    const email = this.form.querySelector('input[name="email"]').value;

    if (!email) {
      this.showErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!this.apiKey) {
      this.showErrorMessage("Klaviyo API key is not configured.");
      return;
    }

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: this.listId,
            },
          },
        },
      },
    };

    fetch(
      "https://a.klaviyo.com/client/subscriptions?company_id=" + this.apiKey,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-10-15",
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) => {
        if (response.ok) {
          this.showSuccessMessage();
        } else {
          return response.json().then((data) => {
            console.error("Klaviyo API error:", data);
            this.showErrorMessage(
              "Sorry, there was an error. Please try again."
            );
          });
        }
      })
      .catch((error) => {
        console.error("Klaviyo subscription error:", error);
        this.showErrorMessage("Sorry, there was an error. Please try again.");
      });
  }

  showSuccessMessage() {
    // Remove any existing messages
    this.clearMessages();

    const successDiv = document.createElement("div");
    successDiv.className = "text-[13px] text-green-600";
    successDiv.textContent = "Thank you for subscribing!";
    this.form.parentNode.insertBefore(successDiv, this.form.nextSibling);
    this.form.reset();
  }

  showErrorMessage(message = "Sorry, there was an error. Please try again.") {
    // Remove any existing messages
    this.clearMessages();

    const errorDiv = document.createElement("div");
    errorDiv.className = "text-[13px] text-red-500";
    errorDiv.textContent = message;
    this.form.parentNode.insertBefore(errorDiv, this.form.nextSibling);
  }

  clearMessages() {
    const existingMessages = this.form.parentNode.querySelectorAll(
      ".text-green-600, .text-red-500"
    );
    existingMessages.forEach((msg) => msg.remove());
  }
}

if (!window.customElements.get("klaviyo-form")) {
  window.customElements.define("klaviyo-form", KlaviyoForm);
}


// Bold Pricing App - Move compare-at price into price-list element
function fixBoldPricingElements() {
  // Find all v-stacks that might contain Bold pricing elements
  document.querySelectorAll('.v-stack, .product-card__info').forEach(container => {
    const moneyDetails = container.querySelector('.money-details:not(.bold-fixed)');
    const priceList = container.querySelector('price-list.money:not(.bold-fixed)');
    
    // Only process if both elements exist and haven't been processed
    if (moneyDetails && priceList) {
      // Get the compare-at price content
      const compareAtContent = moneyDetails.innerHTML;
      
      if (compareAtContent && compareAtContent.trim() !== '') {
        // Create a compare-at-price element
        const comparePriceElement = document.createElement('compare-at-price');
        comparePriceElement.className = 'h6 text-subdued line-through';
        comparePriceElement.style.textDecoration = 'line-through';
        comparePriceElement.style.color = 'rgba(0,0,0,0.65)';
        comparePriceElement.innerHTML = compareAtContent;
        
        // Insert at the beginning of price-list
        priceList.insertBefore(comparePriceElement, priceList.firstChild);
        
        // Remove the money-details div
        moneyDetails.remove();
        
        // Mark as processed
        priceList.classList.add('bold-fixed');
        moneyDetails.classList.add('bold-fixed');
      }
    }
  });
}

// Run on page load
fixBoldPricingElements();

// Use MutationObserver to watch for when Bold injects elements
const observer = new MutationObserver(() => {
  fixBoldPricingElements();
});

// Start observing
document.addEventListener('DOMContentLoaded', () => {
  fixBoldPricingElements();
  observer.observe(document.body, { childList: true, subtree: true });
});

// ── Upsell custom elements ──────────────────────────────────────────

class UpsellProduct extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-size-select]');
    this.hiddenInput = this.querySelector('input[name*="[id]"]');
    this.priceDivs = this.querySelectorAll('[data-variant-price-display]');

    this.select?.addEventListener('change', () => this.handleChange());
  }

  handleChange() {
    const variantId = this.select.value;
    this.hiddenInput.value = variantId;

    this.priceDivs.forEach((div) => {
      div.classList.toggle('hidden', div.dataset.variantId !== variantId);
    });

    this.dispatchEvent(
      new CustomEvent('upsell:variant-change', { bubbles: true })
    );
  }

  get selectedVariantId() {
    return this.hiddenInput?.value || '';
  }

  get selectedPrice() {
    const visible = [...this.priceDivs].find((d) => !d.classList.contains('hidden'));
    return visible ? parseInt(visible.dataset.variantPrice, 10) || 0 : 0;
  }

  get selectedOriginalPrice() {
    const visible = [...this.priceDivs].find((d) => !d.classList.contains('hidden'));
    return visible ? parseInt(visible.dataset.variantOriginalPrice, 10) || 0 : 0;
  }
}

if (!window.customElements.get('upsell-product')) {
  window.customElements.define('upsell-product', UpsellProduct);
}

class UpsellForm extends HTMLElement {
  connectedCallback() {
    this.atcButton = this.querySelector('[data-atc-button]');
    this.totalEl = this.querySelector('[data-total-price]');

    this.atcButton?.addEventListener('click', () => this.handleSubmit());
    this.addEventListener('upsell:variant-change', () => this.updateTotal());

    this.updateTotal();
  }

  get products() {
    return [...this.querySelectorAll('upsell-product')];
  }

  updateTotal() {
    const total = this.products.reduce((sum, p) => sum + p.selectedPrice, 0);
    const originalTotal = this.products.reduce((sum, p) => sum + p.selectedOriginalPrice, 0);
    const fmt = (v) =>
      window.Shopify?.formatMoney
        ? Shopify.formatMoney(v, window.money_format || '${{amount}}')
        : '$' + (v / 100).toFixed(2);
    if (this.totalEl) {
      if (originalTotal > total) {
        this.totalEl.innerHTML =
          `<span class="line-through">${fmt(originalTotal)}</span> <span class="text-red-500">${fmt(total)}</span>`;
      } else {
        this.totalEl.textContent = fmt(total);
      }
    }
  }

  handleSubmit() {
    const groupId = Date.now().toString();
    const mainProductId = this.dataset.mainProductId;    
    const discountPercentage = this.dataset.discountPercentage || '';
    const productCount = this.dataset.productCount || '';
    const items = this.products
      .filter((p) => p.selectedVariantId)
      .map((p) => {
        const props = { _complete_the_look: groupId,          
          _complete_the_look_number: productCount,
          _complete_the_look_discount: discountPercentage, };
        if (p.dataset.productId === mainProductId) {
          props._complete_the_look_main = 'true';
        }
        return {
          id: parseInt(p.selectedVariantId, 10),
          quantity: 1,
          properties: props,
        };
      });

    if (items.length === 0) {
      alert('Please select a size for at least one product.');
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { baseEvent: 'variant:add' },
          })
        );
        document.querySelector('cart-drawer')?.show();
      })
      .catch((err) => {
        console.error(err);
        alert('Something went wrong adding to cart. Please try again.');
      })
      .finally(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
      });
  }
}

if (!window.customElements.get('upsell-form')) {
  window.customElements.define('upsell-form', UpsellForm);
}

class StickyAtcBar extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onScroll.bind(this);
    this._onPurchaseClick = this._onPurchaseClick.bind(this);
    this._rafId = null;
  }

  connectedCallback() {
    // Start hidden; JS will show it only after the user scrolls past `buy-buttons`.
    this.classList.add('translate-y-full');
    this._onScroll();
    this.addEventListener('click', this._onPurchaseClick);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onPurchaseClick);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _getScope() {
    return this.closest('product-rerender') || document;
  }

  _getPurchaseTarget() {
    const scope = this._getScope();
    return (
      scope.querySelector('#pdp-purchase') ||
      scope.querySelector('variant-picker') ||
      scope.querySelector('buy-buttons.buy-buttons')
    );
  }

  _onPurchaseClick(event) {
    const trigger = event.target.closest('[data-scroll-to-purchase]');
    if (!trigger) return;

    event.preventDefault();
    this._scrollToPurchase();
  }

  _scrollToPurchase() {
    const target = this._getPurchaseTarget();
    if (!target) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    target.scrollIntoView({ block: 'start', behavior });

    window.setTimeout(() => {
      const scope = this._getScope();
      const variantPicker = scope.querySelector('variant-picker');
      if (!variantPicker) return;

      const uncheckedSize = variantPicker.querySelector('input[type="radio"]:not(:checked)');
      if (uncheckedSize && !variantPicker.querySelector('input[type="radio"]:checked')) {
        uncheckedSize.focus({ preventScroll: true });
      }
    }, behavior === 'smooth' ? 400 : 0);
  }

  _getBuyButtons() {
    return this._getScope().querySelector('buy-buttons.buy-buttons');
  }

  _onScroll() {
    if (!this.isConnected) return;
    if (this._rafId) return;

    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      const buyButtons = this._getBuyButtons();
      if (!buyButtons) return;

      const rect = buyButtons.getBoundingClientRect();

      // "Scrolled past" means the bottom edge of the buy buttons is above the viewport.
      // When this becomes true, we hide the sticky offset transition by removing `translate-y-full`.
      const hasScrolledPast = rect.bottom <= 0;
      this.classList.toggle('translate-y-full', !hasScrolledPast);
    });
  }
}

if (!window.customElements.get('sticky-atc-bar')) {
  window.customElements.define('sticky-atc-bar', StickyAtcBar);
}

/* ROYAL: open the inline quick-add on ANY card. The standard <product-card> has no <new-product-card>
   delegated trigger, and quick-add's own direct listener can miss when children parse after connectedCallback.
   This delegated handler opens it everywhere; QuickAdd handles close (mouseleave/scroll), QuickAddForm the add + redirect to cart. */
(function () {
  if (window.__qaTriggerGlobal) return;
  window.__qaTriggerGlobal = true;
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;
    e.preventDefault();
    var qa = trigger.closest('quick-add');
    if (!qa) return;
    document.querySelectorAll('quick-add.active').forEach(function (el) { if (el !== qa) el.classList.remove('active'); });
    if (typeof qa.show === 'function') qa.show(); else qa.classList.add('active');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var qa = document.querySelector('quick-add.active');
    if (qa && typeof qa.hide === 'function') qa.hide();
  });
})();

/* Keep empty-cart recommendations from being vertically centered into the header. */
(function () {
  function syncEmptyCartDrawer(drawer) {
    if (!drawer || !drawer.querySelector('.cart-drawer__empty-recs')) return;
    drawer.classList.remove('drawer--center-body');
  }
  document.addEventListener('cart-drawer:refreshed', function (e) {
    syncEmptyCartDrawer(e.target);
  });
  document.addEventListener('DOMContentLoaded', function () {
    syncEmptyCartDrawer(document.querySelector('cart-drawer'));
  });
})();

/* PDP accordion engagement. Only drawers with data-accordion-id are tracked.
   accordion_name is the visible title, so a rename updates reporting without a code change. */
document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  const accordionId = details.dataset.accordionId;
  if (!accordionId) return;

  const title = details.querySelector('[data-accordion-title]');
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'accordion_toggle',
    accordion_id: accordionId,
    accordion_name: (title?.textContent || '').replace(/\s+/g, ' ').trim(),
    accordion_action: details.open ? 'expand' : 'collapse',
    item_id: details.dataset.itemId || '',
    item_name: details.dataset.itemName || '',
  });
}, true);

/* PDP rating summary → scroll to Okendo reviews section.
   Prefer #product-reviews; never fall back to star-rating chips (.oke-sr),
   which sit near the top of the PDP and make the click look like a no-op. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-scroll-to-reviews], a[href="#product-reviews"]');
  if (!trigger) return;

  event.preventDefault();

  const target =
    document.getElementById('product-reviews') ||
    document.querySelector('[data-product-reviews-anchor]') ||
    document.querySelector('[data-oke-reviews-widget]') ||
    document.querySelector('[id*="okendo_reviews_widget"]') ||
    document.querySelector('.okeReviews.oke-w') ||
    document.querySelector('.shopify-section--apps .okeReviews:not(.oke-sr)');

  if (!target) return;

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (history.replaceState) {
    history.replaceState(null, '', '#product-reviews');
  }
});
